/* eslint-disable no-use-before-define */
import Ajv from 'ajv';
import assign from 'lodash/assign';
import castArray from 'lodash/castArray';
import cloneDeep from 'lodash/cloneDeep';
import entries from 'lodash/entries';
import find from 'lodash/find';
import first from 'lodash/first';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import last from 'lodash/last';
import memoize from 'lodash/memoize';
import merge from 'lodash/merge';
import remove from 'lodash/remove';
import some from 'lodash/some';
import { requiredArg } from './utils';

const ajv = new Ajv({
  unicodeRegExp: false,
  code: { es5: true },
  strictTypes: false,
  strict: true,
});

export const getDefinitionMatchingEnum = (
  inputValue,
  parentPropertyName,
  oneOf
) =>
  find(oneOf, definition => {
    if (typeof inputValue === 'undefined') return false;

    const enumValues = castArray(
      get(
        definition,
        ['properties', parentPropertyName, 'enum'],
        get(definition, ['properties', parentPropertyName, 'const'])
      )
    );

    return some(enumValues, enumValue => isEqual(inputValue, enumValue));
  });

const getAllOfMatchers = (schemaNode, path = []) => {
  if (schemaNode.allOf) {
    const { allOf = [] } = schemaNode;

    const matchers = allOf.reduce((acc, subSchemaNode, index) => {
      const matchersAtSchemaNode = [
        ...getIfThenElseMatcher(subSchemaNode, [...path, 'allOf', index]),
        ...getAllOfMatchers(subSchemaNode, [...path, 'allOf', index]),
      ];

      if (subSchemaNode.properties) {
        acc = [
          ...acc,
          {
            keyword: 'allOf.properties',
            parentDependencies: [
              [last(keys(schemaNode.properties)), ...path, 'allOf', index],
            ],
            dependentProperties: subSchemaNode.properties,
            dependentPropertyNames: keys(subSchemaNode.properties),
          },
        ];
      }

      if (matchersAtSchemaNode.length > 0) {
        acc = [
          ...acc,
          ...matchersAtSchemaNode.map(
            matcher =>
              (values = {}, pageLevel = false) => {
                const resolveDependency = dependency =>
                  /^(then|else)$/.test(dependency.keyword)
                    ? {
                        ...dependency,
                        keyword: `allOf.${dependency.keyword}`,
                        parentDependencies: [
                          [...first(dependency.parentDependencies)],
                        ],
                      }
                    : dependency;

                const result =
                  typeof matcher === 'function'
                    ? matcher(values, pageLevel)
                    : matcher;

                if (Array.isArray(result)) {
                  return result.map(resolveDependency);
                }

                return resolveDependency(result);
              }
          ),
        ];
      }

      return acc;
    }, []);

    return matchers;
  }

  return [];
};

const getParentPropertyName = schemaNode => {
  if (isArray(schemaNode)) {
    return getParentPropertyName(last(schemaNode));
  }

  if (has(schemaNode, 'properties')) {
    const parentPropertyNames = keys(schemaNode.properties);
    return last(parentPropertyNames);
  }

  return null;
};

const getIfThenElseMatcher = (schemaNode, path = []) => {
  if (schemaNode.if && (schemaNode.then || schemaNode.else)) {
    const matcher = (values = {}, pageLevel = false) => {
      let parentPropertyName = null;

      if (has(schemaNode, ['if', 'properties'])) {
        parentPropertyName = getParentPropertyName(schemaNode.if);
      } else if (has(schemaNode, ['if', 'anyOf'])) {
        parentPropertyName = getParentPropertyName(schemaNode.if.anyOf);
      } else if (has(schemaNode, ['if', 'oneOf'])) {
        parentPropertyName = getParentPropertyName(schemaNode.if.oneOf);
      }

      const keyword = ajv.validate(schemaNode.if, values) ? 'then' : 'else';
      const dependentSchema = get(schemaNode, keyword, {});
      const dependentProperties = dependentSchema.properties;

      const activeDependencies = memoizedDependencyMatcher(
        dependentSchema,
        [...path, keyword],
        (schemaNode, path) => [
          ...getIfThenElseMatcher(schemaNode, path),
          ...getAllOfMatchers(schemaNode, path),
        ]
      )(values);

      const dependencies = [
        ...activeDependencies,
        ...(dependentProperties
          ? [
              ...(pageLevel
                ? entries(dependentProperties).reduce(
                    (acc, [parentPageName, node]) => {
                      const dependentPageProperties = node.properties;

                      return [
                        ...acc,
                        {
                          keyword,
                          parentPageName,
                          parentDependencies: [[null, ...path]],
                          dependentProperties: dependentPageProperties,
                          dependentPropertyNames: keys(dependentPageProperties),
                        },
                      ];
                    },
                    []
                  )
                : [
                    {
                      keyword,
                      parentDependencies: [[parentPropertyName, ...path]],
                      dependentProperties,
                      dependentPropertyNames: keys(dependentProperties),
                    },
                  ]),
            ]
          : []),
      ];

      return dependencies;
    };

    return [matcher];
  }

  return [];
};

export const memoizedDependencyMatcher = memoize(
  (
    schemaNode = requiredArg('schemaNode'),
    path = [],
    createMatchers = (schemaNode, path) => [
      ...getIfThenElseMatcher(schemaNode, path),
      ...getAllOfMatchers(schemaNode, path),
    ]
  ) => {
    const matchers = createMatchers(schemaNode, path);

    const getActiveDependencies = (
      values = requiredArg('values'),
      pageLevel = false
    ) =>
      flatMap(matchers, matcher => {
        if (typeof matcher === 'function') {
          return matcher(values, pageLevel);
        }

        return matcher;
      });

    getActiveDependencies.matcherLength = matchers.length;
    getActiveDependencies.matchers = matchers;

    return getActiveDependencies;
  }
);

export const pageLevelDependenciesReducer = pageLevelDependencies => {
  let remainder = [...pageLevelDependencies];

  const getActivePageLevelDependencies = currentPageName => {
    if (remainder.length === 0) return [];

    return remove(remainder, dep => dep.parentPageName === currentPageName);
  };

  getActivePageLevelDependencies.dependenciesLength = remainder.length;

  return getActivePageLevelDependencies;
};

export const combineProperties = (
  nodeProperties = {},
  activeDependencies = [],
  pageLevelActiveDependencies = []
) => {
  let propertyNames = keys(nodeProperties);
  let properties = cloneDeep(nodeProperties);

  for (const { dependentProperties } of pageLevelActiveDependencies) {
    properties = merge(properties, dependentProperties);
    propertyNames = keys(properties);
  }

  for (const {
    dependentProperties,
    parentDependencies,
  } of activeDependencies) {
    const parentPropertyName = last(parentDependencies)[0];

    propertyNames.splice(
      propertyNames.indexOf(parentPropertyName) + 1,
      0,
      ...keys(dependentProperties)
    );

    assign(properties, dependentProperties);
  }

  return { propertyNames, properties };
};
